import { useState, useEffect, useCallback } from "react";
import { Loader2, TrendingUp, Wallet, Repeat, Users, Target, Info, Layers, Activity, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { toast } from "sonner";

const money = (n) => `$${(n ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const pct = (n) => `${(n ?? 0).toFixed(1)}%`;

function Stat({ label, value, sub, tone, testid }) {
  const color = tone === "profit" ? (value < 0 ? "text-[#EF4444]" : "text-[#10B981]")
    : tone === "gold" ? "text-[#D4AF37]" : tone === "in" ? "text-[#10B981]" : "text-white";
  return (
    <div className="rounded-xl border border-white/10 bg-[#121214] p-4" data-testid={testid}>
      <div className={`text-2xl font-heading ${color}`}>{typeof value === "number" ? money(value) : value}</div>
      <div className="text-xs text-[#71717A] mt-1">{label}</div>
      {sub ? <div className="text-[11px] text-[#52525B] mt-0.5">{sub}</div> : null}
    </div>
  );
}

function Row({ title, icon: Icon, children }) {
  return (
    <div>
      <div className="flex items-center gap-2 mb-2 mt-4">
        <Icon className="w-4 h-4 text-[#D4AF37]" />
        <h3 className="font-heading text-sm text-white uppercase tracking-wide">{title}</h3>
      </div>
      {children}
    </div>
  );
}

function BarTrend({ data, valueKey, testid }) {
  const max = Math.max(1, ...data.map((d) => d[valueKey] || 0));
  return (
    <div className="rounded-xl border border-white/10 bg-[#121214] p-4" data-testid={testid}>
      <div className="flex items-end gap-1 h-28">
        {data.map((d, i) => {
          const h = Math.max(2, ((d[valueKey] || 0) / max) * 100);
          const neg = (d[valueKey] || 0) < 0;
          return (
            <div key={i} className="flex-1 flex flex-col justify-end group relative" title={`${d.date || d.month}: ${money(d[valueKey])}`}>
              <div className={`rounded-t ${neg ? "bg-[#EF4444]" : "bg-[#D4AF37]"} transition-all`} style={{ height: `${h}%`, opacity: (d[valueKey] || 0) === 0 ? 0.15 : 0.85 }} />
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function AdminProfit() {
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  const [range, setRange] = useState("30d");
  const [syncing, setSyncing] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/profit");
      setD(r.data);
    } catch (e) { toast.error("Failed to load executive scorecard"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const syncStripe = async () => {
    setSyncing(true);
    try {
      const r = await api.post("/admin/stripe/sync");
      toast.success(r.data.synced ? `Synced from Stripe · ${r.data.backfilled} records attributed` : "Sync ran (no Stripe data available)");
      await load();
    } catch (e) { toast.error("Stripe sync failed"); }
    finally { setSyncing(false); }
  };

  if (loading || !d) return <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-[#D4AF37]" /></div>;

  const { subscribers, recurring, cash, expenses, estimated_profit: ep, attribution: attr, health, trends } = d;
  const feesActual = expenses.fees_source === "stripe_actual";

  return (
    <div className="space-y-4" data-testid="admin-profit">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="font-heading text-2xl text-white">Executive Scorecard</h2>
          <p className="text-sm text-[#71717A]">Traffic → Leads → Nurture → Paid Subscribers → Recurring Revenue → Cash Flow → Profit.</p>
        </div>
        <div className="text-right">
          <Button onClick={syncStripe} disabled={syncing} data-testid="profit-sync-btn"
            className="fv-gold-gradient text-[#0A0A0B] font-semibold rounded-lg">
            {syncing ? <Loader2 className="w-4 h-4 animate-spin" /> : <><RefreshCw className="w-4 h-4 mr-1.5" /> Sync from Stripe</>}
          </Button>
          <div className="text-[11px] text-[#52525B] mt-1" data-testid="profit-fees-source">
            {feesActual ? `Fees: actual (synced ${expenses.fees_synced_at ? new Date(expenses.fees_synced_at).toLocaleDateString() : ""})` : "Fees: estimated (2.9% + $0.30/txn)"}
          </div>
        </div>
      </div>

      <div className="flex items-start gap-2 rounded-lg border border-[#D4AF37]/20 bg-[#D4AF37]/5 p-3" data-testid="profit-disclaimer">
        <Info className="w-4 h-4 text-[#D4AF37] mt-0.5 shrink-0" />
        <p className="text-xs text-[#A1A1AA]">{d.label}</p>
      </div>

      {/* Morning snapshot */}
      <Row title="This Morning at a Glance" icon={Activity}>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Stat testid="profit-new-subs-today" label="New Paid Subscribers Today" value={String(subscribers.new_today)} tone="gold" sub={`${subscribers.new_week} this week`} />
          <Stat testid="profit-new-mrr" label="New MRR Today" value={recurring.new_mrr_today} tone="gold" />
          <Stat testid="profit-cash-today" label="Cash Collected Today" value={cash.today} tone="in" />
          <Stat testid="profit-netcf-today" label="Net Cash Flow Today" value={cash.net_flow_today} tone="profit" />
          <Stat testid="profit-subs" label="Total Paid Subscribers" value={String(subscribers.verified_paid)} tone="gold" />
          <Stat testid="profit-mrr" label="Total MRR" value={recurring.mrr} tone="gold" sub={`ARR ${money(recurring.arr)}`} />
          <Stat testid="profit-cash-month" label="Cash Collected This Month" value={cash.month} tone="in" />
          <Stat testid="profit-netcf-month" label="Net Cash Flow This Month" value={cash.net_flow_month} tone="profit" />
        </div>
      </Row>

      {/* Estimated profit */}
      <Row title="Estimated Profit — based on tracked expenses" icon={TrendingUp}>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Stat testid="profit-today" label="Est. Profit Today" value={ep.today} tone="profit" />
          <Stat testid="profit-week" label="Est. Profit This Week" value={ep.week} tone="profit" />
          <Stat testid="profit-month" label="Est. Profit This Month" value={ep.month} tone="profit" sub={`Margin ${pct(ep.margin_month_pct)}`} />
          <Stat testid="profit-ytd" label="Est. Profit YTD" value={ep.ytd} tone="profit" sub={`Margin ${pct(ep.margin_ytd_pct)}`} />
        </div>
      </Row>

      {/* Trends */}
      <Row title="Trends (real data only)" icon={TrendingUp}>
        <div className="flex gap-1.5 mb-2">
          {["7d", "30d", "ytd"].map((r) => (
            <button key={r} data-testid={`profit-trend-${r}`} onClick={() => setRange(r)}
              className={`text-xs px-3 py-1 rounded-full border ${range === r ? "border-[#D4AF37] text-[#D4AF37] bg-[#D4AF37]/10" : "border-white/10 text-[#71717A]"}`}>
              {r === "7d" ? "7 days" : r === "30d" ? "30 days" : "YTD"}
            </button>
          ))}
        </div>
        {range === "ytd"
          ? <BarTrend data={trends.ytd_monthly} valueKey="est_profit" testid="profit-trend-chart" />
          : <BarTrend data={range === "7d" ? trends.cash_7d : trends.cash_30d} valueKey="cash" testid="profit-trend-chart" />}
        <p className="text-[11px] text-[#52525B] mt-1">{range === "ytd" ? "Estimated profit by month (cash − tracked expenses − est. fees)." : "Cash collected per day."}</p>
      </Row>

      {/* Recurring revenue breakdown */}
      <Row title="Recurring Revenue by Plan & Cycle" icon={Repeat}>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {recurring.by_plan.map((p) => (
            <Stat key={p.plan} testid={`profit-plan-${p.plan}`} label={`${p.plan[0].toUpperCase() + p.plan.slice(1)} MRR`} value={p.mrr} tone="gold" sub={`${p.count} subscriber${p.count === 1 ? "" : "s"}`} />
          ))}
        </div>
        <div className="grid grid-cols-2 gap-3 mt-3">
          <Stat testid="profit-cycle-monthly" label="Monthly-plan MRR" value={recurring.by_cycle.monthly.mrr} tone="gold" sub={`${recurring.by_cycle.monthly.count} on monthly`} />
          <Stat testid="profit-cycle-annual" label="Annual-plan MRR" value={recurring.by_cycle.annual.mrr} tone="gold" sub={`${recurring.by_cycle.annual.count} on annual`} />
        </div>
      </Row>

      {/* Cash windows */}
      <Row title="Cash Collected" icon={Wallet}>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Stat testid="profit-cash-week" label="Cash This Week" value={cash.week} tone="in" />
          <Stat testid="profit-cash-month2" label="Cash This Month" value={cash.month} tone="in" />
          <Stat testid="profit-cash-ytd" label="Cash YTD" value={cash.ytd} tone="in" />
          <Stat testid="profit-exp-ytd" label="Tracked Expenses YTD" value={expenses.ytd} />
        </div>
        {cash.unverified_ytd > 0 && (
          <div className="mt-3 flex items-start gap-2 rounded-lg border border-white/10 bg-[#0A0A0B] p-3" data-testid="profit-unverified">
            <Info className="w-4 h-4 text-[#71717A] mt-0.5 shrink-0" />
            <p className="text-xs text-[#71717A]"><span className="text-[#A1A1AA]">{money(cash.unverified_ytd)}</span> of seed/demo/test payment records exist but are <b>excluded</b> from all real revenue, cash, and profit KPIs (not verified against live Stripe).</p>
          </div>
        )}
      </Row>

      {/* Tracked costs */}
      <Row title="Tracked Costs & Deductions" icon={Layers}>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Stat testid="profit-exp-month" label="Tracked Expenses (mo)" value={expenses.month} />
          <Stat testid="profit-fees-month" label={feesActual ? "Stripe Fees (mo, actual)" : "Est. Stripe Fees (mo)"} value={expenses.processing_fees_month} sub={feesActual ? "Synced from Stripe" : "2.9% + $0.30/txn — estimate"} />
          <Stat testid="profit-refunds-month" label={feesActual ? "Refunds (mo, actual)" : "Refunds Logged (mo)"} value={expenses.refunds_month} />
          <div className="rounded-xl border border-white/10 bg-[#121214] p-4" data-testid="profit-discounts">
            <div className="text-2xl font-heading text-[#71717A]">Not tracked</div>
            <div className="text-xs text-[#71717A] mt-1">Discounts / Credits</div>
            <div className="text-[11px] text-[#52525B] mt-0.5">No reliable data source</div>
          </div>
        </div>
      </Row>

      {/* Health */}
      <Row title="Subscriber Health & Conversion" icon={Users}>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Stat testid="profit-conversion" label="Conversion Rate" value={pct(health.conversion_pct)} tone="gold" sub={`${subscribers.verified_paid}/${health.total_accounts} accounts`} />
          <Stat testid="profit-churn" label="Churn Rate" value={pct(health.churn_pct)} />
          <Stat testid="profit-cancellations" label="Cancellations" value={String(health.cancellations)} />
          <Stat testid="profit-arr2" label="ARR" value={recurring.arr} tone="gold" />
        </div>
      </Row>

      {/* Acquisition attribution */}
      <Row title="Which Channels Generate Revenue" icon={Target}>
        <div className="grid grid-cols-2 gap-3">
          <Stat testid="profit-best-source" label="Best Source (by MRR)" value={attr.best_source} tone="gold" />
          <Stat testid="profit-best-tool" label="Best Converting Tool" value={attr.best_tool} tone="gold" />
        </div>
        <div className="mt-3 rounded-xl border border-white/10 bg-[#121214] p-4 overflow-x-auto" data-testid="profit-source-breakdown">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[#71717A] text-xs text-left">
                <th className="pb-2 font-normal">Source</th>
                <th className="pb-2 font-normal text-right">Paid subs</th>
                <th className="pb-2 font-normal text-right">MRR</th>
                <th className="pb-2 font-normal text-right">Cash (mo)</th>
                <th className="pb-2 font-normal text-right">Cash (YTD)</th>
              </tr>
            </thead>
            <tbody>
              {attr.by_source.map((s) => (
                <tr key={s.source} className="border-t border-white/5" data-testid={`profit-src-row-${s.source}`}>
                  <td className="py-1.5 text-[#D4D4D8] capitalize">{s.source === "unknown" ? <span className="text-[#71717A]">unattributed</span> : s.source}</td>
                  <td className="py-1.5 text-right text-[#D4D4D8]">{s.subscribers}</td>
                  <td className="py-1.5 text-right text-[#D4AF37]">{money(s.mrr)}</td>
                  <td className="py-1.5 text-right text-[#10B981]">{money(s.cash_month)}</td>
                  <td className="py-1.5 text-right text-[#10B981]">{money(s.cash_ytd)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="text-[11px] text-[#52525B] mt-2">"Unattributed" = payments with no known acquisition source. We never invent attribution. Operating expenses aren't source-specific, so profit-by-source isn't shown.</p>
        </div>
      </Row>
    </div>
  );
}
